/* File: permissionResolver.js
 * Path: ananunadm/src/helpers/permissionResolver.js
 *
 * Order: ananunaki → calendar/security → deny → allow → role keys (nested + flatPermissions).
 */

import {
	collectKeysFromUserRoles,
	collectExplicitFieldKeysFromUserRoles,
	FORMS_KEY_IMPLIED_BY_CRUD,
	expandFormsCrudInKeySet
} from "./flattenPermissionKeys";
import { isAnanunakiSuperuser } from "./ananunaki";

function mergedDeny(user) {
	const s = new Set();
	(user?.permissions?.deny || []).forEach((k) => s.add(k));
	(user?.permissionOverrides?.deny || []).forEach((k) => s.add(k));
	return s;
}

function mergedAllow(user) {
	const s = new Set();
	(user?.permissions?.allow || []).forEach((k) => s.add(k));
	(user?.permissionOverrides?.allow || []).forEach((k) => s.add(k));
	return s;
}

function getRoleKeySet(user) {
	if (!user) return new Set();
	if (user._rolePermissionKeySet) return user._rolePermissionKeySet;
	if (Array.isArray(user.allPermissions) && user.allPermissions.length) {
		const s = new Set(user.allPermissions);
		// Stale sessions may only have forms.read/update CRUD — expand in place.
		if (s.has("forms.read") || s.has("forms.update") || s.has("forms.delete")) {
			expandFormsCrudInKeySet(s);
		}
		user._rolePermissionKeySet = s;
		return s;
	}
	const s = collectKeysFromUserRoles(user);
	user._rolePermissionKeySet = s;
	return s;
}

/** Field keys the role explicitly toggled (checked or unchecked) — distinct from granted keys. */
function getExplicitFieldKeySet(user) {
	if (!user) return new Set();
	if (user._roleExplicitFieldKeySet) return user._roleExplicitFieldKeySet;
	if (Array.isArray(user.allConfiguredFieldKeys) && user.allConfiguredFieldKeys.length) {
		const s = new Set(user.allConfiguredFieldKeys);
		user._roleExplicitFieldKeySet = s;
		return s;
	}
	const s = collectExplicitFieldKeysFromUserRoles(user);
	user._roleExplicitFieldKeySet = s;
	return s;
}

export function hasPermission(user, permissionKey) {
	if (!user || !permissionKey) return false;
	if (isAnanunakiSuperuser(user)) return true;

	const [mod] = permissionKey.split(".");
	if (mod === "calendar" || mod === "security") return true;

	if (mergedDeny(user).has(permissionKey)) return false;
	if (mergedAllow(user).has(permissionKey)) return true;

	if (getRoleKeySet(user).has(permissionKey)) return true;

	const formsCrud = FORMS_KEY_IMPLIED_BY_CRUD[permissionKey];
	if (formsCrud && !mergedDeny(user).has(formsCrud)) {
		if (mergedAllow(user).has(formsCrud) || getRoleKeySet(user).has(formsCrud)) {
			return true;
		}
	}

	return false;
}

/** True if role grants any field-level *.read under this module (e.g. rom.index.read), excluding the module row itself. */
function hasAnyFieldReadForModule(user, moduleKey) {
	if (!user || !moduleKey) return false;
	const deny = mergedDeny(user);
	const set = getRoleKeySet(user);
	const allow = mergedAllow(user);
	const moduleRead = `${moduleKey}.read`;
	const prefix = `${moduleKey}.`;

	const keyOk = (k) => {
		if (!k || typeof k !== "string") return false;
		if (k === moduleRead) return false;
		if (!k.startsWith(prefix) || !k.endsWith(".read")) return false;
		if (deny.has(k)) return false;
		return set.has(k) || allow.has(k);
	};

	for (const k of set) {
		if (keyOk(k)) return true;
	}
	for (const k of allow) {
		if (keyOk(k)) return true;
	}
	return false;
}

/**
 * A dotted permission model can represent a scoped section, such as
 * `office.rooms`. The backend treats `office.read` as the broad read grant for
 * every Office section, so mirror that behavior for menu, route, and page
 * visibility without broadening create/update/delete permissions.
 */
function hasScopedOrParentRead(user, moduleKey) {
	if (!moduleKey) return false;
	if (hasPermission(user, `${moduleKey}.read`)) return true;
	if (hasPermission(user, `${moduleKey}.view`)) return true;

	const [parentModule] = moduleKey.split(".");
	if (parentModule !== moduleKey && hasPermission(user, `${parentModule}.read`)) {
		return true;
	}

	return false;
}

/**
 * Sidebar / menu: show module if module.read OR any granted field read (rom.name.read, etc.).
 * Matches how ListPage field visibility works without requiring the module-level read checkbox.
 */
export function userHasModuleMenuAccess(user, moduleKey) {
	if (!moduleKey) return false;
	if (isAnanunakiSuperuser(user)) return true;
	if (moduleKey === "knowledge") {
		return hasPermission(user, "knowledge.read");
	}
	if (hasScopedOrParentRead(user, moduleKey)) return true;
	return hasAnyFieldReadForModule(user, moduleKey);
}

export function hasFieldPermission(user, module, field, action) {
	if (!user || !module || !field || !action) return false;
	if (isAnanunakiSuperuser(user)) return true;
	if (module === "calendar" || module === "security") return true;

	const fieldKey = `${module}.${field}.${action}`;
	if (mergedDeny(user).has(fieldKey)) return false;
	if (hasPermission(user, fieldKey)) return true;

	// A field the role explicitly toggled (even to unchecked) is a deliberate
	// decision, not "unconfigured" — it must NOT fall back to the module-level
	// permission, or unchecking a field's boxes in the role editor has no
	// effect whenever the module-level box is still checked.
	const allow = mergedAllow(user);
	const explicitlyConfigured =
		getExplicitFieldKeySet(user).has(fieldKey) ||
		allow.has(`${module}.${field}.read`) ||
		allow.has(`${module}.${field}.update`) ||
		allow.has(`${module}.${field}.create`) ||
		allow.has(`${module}.${field}.delete`);

	if (!explicitlyConfigured) {
		if (hasPermission(user, `${module}.${action}`)) return true;
		// Broad parent read is the legacy/full-module read grant. Mutating
		// actions stay scoped because the Office API requires the exact key.
		if (action === "read") {
			const [parentModule] = module.split(".");
			return (
				parentModule !== module &&
				hasPermission(user, `${parentModule}.read`)
			);
		}
	}

	return false;
}

export function getModuleCRUD(user, module) {
	return {
		create: hasPermission(user, `${module}.create`),
		read: hasScopedOrParentRead(user, module),
		update: hasPermission(user, `${module}.update`),
		delete: hasPermission(user, `${module}.delete`)
	};
}

export function getFieldCRUD(user, module, field) {
	return {
		create: hasFieldPermission(user, module, field, "create"),
		read: hasFieldPermission(user, module, field, "read"),
		update: hasFieldPermission(user, module, field, "update"),
		delete: hasFieldPermission(user, module, field, "delete")
	};
}

/** Module names the user can see in menu / platform (module.read or any *.field.read). Denied keys never add. */
export function getModuleNamesWithReadAccess(user) {
	if (!user) return [];
	if (isAnanunakiSuperuser(user)) return null;
	const deny = mergedDeny(user);
	const mods = new Set();
	const tryAdd = (key) => {
		if (!key || typeof key !== "string" || deny.has(key)) return;
		const parts = key.split(".");
		if (parts.length === 2 && parts[1] === "read") mods.add(parts[0]);
	};
	/** e.g. rom.index.read → rom (platform accordion visibility). */
	const tryAddFieldRead = (key) => {
		if (!key || typeof key !== "string" || deny.has(key)) return;
		const parts = key.split(".");
		if (parts.length >= 3 && parts[parts.length - 1] === "read") {
			// knowledge.edit.read is an article-edit field, not module access.
			if (parts[0] === "knowledge") return;
			mods.add(parts[0]);
		}
	};
	getRoleKeySet(user).forEach(tryAdd);
	getRoleKeySet(user).forEach(tryAddFieldRead);
	mergedAllow(user).forEach(tryAdd);
	mergedAllow(user).forEach(tryAddFieldRead);
	return Array.from(mods);
}

export function applyInputFieldPermissions(user, moduleKey, input) {
	if (!input) return input;
	if (input.divider) return input;
	input.crud = getFieldCRUD(user, moduleKey, input.permissionModel);
	// Read gates visibility: no read access => don't render the field at all.
	if (input.permissionModel && !input.crud.read) {
		return undefined;
	}
	// Update gates editability: visible but locked, not hidden. Only adds
	// disablement — never clears a `disabled` the form already set for an
	// unrelated reason.
	if (input.permissionModel && !input.crud.update) {
		input.disabled = true;
	}
	return input;
}

/**
 * True if the user can access any ticket list/detail route.
 * Checks: ticket, ticketMine, ticketDashboard, or any ticketsDomain_* / ticketsType_* read.
 */
export function userHasAnyTicketAccess(user) {
	if (!user) return false;
	if (isAnanunakiSuperuser(user)) return true;
	if (userHasModuleMenuAccess(user, "ticket")) return true;
	if (userHasModuleMenuAccess(user, "ticketMine")) return true;
	if (userHasModuleMenuAccess(user, "ticketDashboard")) return true;
	const deny = mergedDeny(user);
	const keys = getRoleKeySet(user);
	const allow = mergedAllow(user);
	const check = (k) =>
		k &&
		(k.startsWith("ticketsDomain_") || k.startsWith("ticketsType_")) &&
		k.endsWith(".read") &&
		!deny.has(k);
	for (const k of keys) if (check(k)) return true;
	for (const k of allow) if (check(k)) return true;
	return false;
}

export function clearPermissionCache(user) {
	if (user) {
		delete user._rolePermissionKeySet;
		delete user._roleExplicitFieldKeySet;
		delete user._resolvedPermissions;
	}
}

export { isAnanunakiSuperuser } from "./ananunaki";

export default {
	hasPermission,
	hasFieldPermission,
	getModuleCRUD,
	getFieldCRUD,
	getModuleNamesWithReadAccess,
	userHasModuleMenuAccess,
	applyInputFieldPermissions,
	clearPermissionCache,
	isAnanunakiSuperuser
};
